// src/services/routingRulesService.ts
//
// All Supabase access for the yak_routing_rules table (Story 1.5). Rules
// editing is admin config: direct table writes under RLS (spine AD-3) — NOT a
// tx_ transition RPC, so no p_actor / {ok,code,data} envelope. The SELECT
// policy covers all org members (Epic 4 reads the layer list); the UPDATE
// policy requires an admin role. Rows are never created or deleted from any
// client (default-deny) — the five per org are seeded DB-side.
//
// Components/hooks consume the clean src/types/yak.ts domain types only; the
// generated type for the `layers` jsonb column is `Json`, so results are cast
// once per method at this boundary. DB error codes are mapped to stable human
// copy here (the 1.1 throwMapped contract) — raw Postgres text never reaches
// the UI.

import { supabase } from '../lib/supabase'
import type { Json } from '../lib/supabase.types'
import type { YakRoutingRule, YakRuleLayer } from '../types/yak'
import { teamService } from './teamService'

const INVALID_RULE_MESSAGE =
  "That rule couldn't be saved — refresh and try again"
const STALE_ROW_MESSAGE =
  'This rule no longer exists or you no longer have access — refresh and try again'

interface PostgrestErrorLike {
  message: string
  code?: string
}

// Exported for unit testing. The codes reachable from this screen: 23514 from
// the validation trigger (the console pre-validates layer shape, so this
// signals a concurrent edit or a client bug — show stable copy rather than the
// technical trigger text), and PGRST116 when a 0-row update means the row is
// hidden by RLS or the caller's role changed mid-session.
export function throwMapped(error: PostgrestErrorLike): never {
  if (error.code === '23514') throw new Error(INVALID_RULE_MESSAGE)
  if (error.code === 'PGRST116') throw new Error(STALE_ROW_MESSAGE)
  throw new Error(error.message)
}

class RoutingRulesService {
  // All five rule rows for the caller's org (RLS scopes them). The UI orders
  // by QUESTION_TYPES, so no ORDER BY is needed here.
  async listRules(): Promise<YakRoutingRule[]> {
    const { data, error } = await supabase.from('yak_routing_rules').select('*')

    if (error) throw new Error(error.message)
    return (data as unknown as YakRoutingRule[] | null) || []
  }

  // Save the whole layers array in one UPDATE (last-write-wins on the array —
  // the accepted single-admin pilot posture). The row can't be created or
  // deleted, only its layers updated.
  async updateLayers(
    id: string,
    layers: YakRuleLayer[]
  ): Promise<YakRoutingRule> {
    const { data, error } = await supabase
      .from('yak_routing_rules')
      .update({ layers: layers as unknown as Json })
      .eq('id', id)
      .select('*')
      .single()

    if (error) throwMapped(error)
    return data as unknown as YakRoutingRule
  }

  // Active MA users grouped by trimmed position label — the input to AC 5's
  // no-matching-users warning. Reuses Story 1.4's teamService.listTeam()
  // (position lives on the MA user; active = status === 'active') rather than
  // duplicating the query. An org with no MA users yet yields {}, which
  // correctly warns on every layer.
  async listActivePositionCounts(): Promise<Record<string, number>> {
    const members = await teamService.listTeam()
    const counts: Record<string, number> = {}
    for (const member of members) {
      if (member.status !== 'active') continue
      const position = member.position?.trim()
      if (!position) continue
      counts[position] = (counts[position] ?? 0) + 1
    }
    return counts
  }
}

export const routingRulesService = new RoutingRulesService()
